import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Settings2 } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { ErrorState } from '@/components/common/States';
import { Spinner } from '@/components/common/Spinner';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { transportSettingsApi } from '@/services/api/endpoints/transport';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { TransportSettings, TransportSettingsInput } from '@/types/transport';

/** The form's working copy. Numbers as strings, the way inputs hold them. */
interface Draft {
  capacityWarningPercent: string;
  documentExpiryWarningDays: string;
  maintenanceReminderDays: string;
  notifyEnrollment: boolean;
  notifyBoarding: boolean;
  notifyDropOff: boolean;
  notifyDelay: boolean;
  notifyIncident: boolean;
  allowGuestRiders: boolean;
}

function toDraft(settings: TransportSettings): Draft {
  return {
    capacityWarningPercent: String(settings.capacityWarningPercent),
    documentExpiryWarningDays: String(settings.documentExpiryWarningDays),
    maintenanceReminderDays: String(settings.maintenanceReminderDays),
    notifyEnrollment: settings.notifyEnrollment,
    notifyBoarding: settings.notifyBoarding,
    notifyDropOff: settings.notifyDropOff,
    notifyDelay: settings.notifyDelay,
    notifyIncident: settings.notifyIncident,
    allowGuestRiders: settings.allowGuestRiders,
  };
}

/**
 * Only what actually changed. The PUT is a partial update — omitted fields
 * keep their value on the server — so sending an untouched field back is at
 * best noise and at worst a lost concurrent edit.
 */
function changedFields(settings: TransportSettings, draft: Draft): TransportSettingsInput {
  const payload: TransportSettingsInput = {};

  const capacity = draft.capacityWarningPercent === '' ? NaN : Number(draft.capacityWarningPercent);
  if (!Number.isNaN(capacity) && capacity !== settings.capacityWarningPercent) {
    payload.capacityWarningPercent = capacity;
  }
  const expiry =
    draft.documentExpiryWarningDays === '' ? NaN : Number(draft.documentExpiryWarningDays);
  if (!Number.isNaN(expiry) && expiry !== settings.documentExpiryWarningDays) {
    payload.documentExpiryWarningDays = expiry;
  }
  const reminder =
    draft.maintenanceReminderDays === '' ? NaN : Number(draft.maintenanceReminderDays);
  if (!Number.isNaN(reminder) && reminder !== settings.maintenanceReminderDays) {
    payload.maintenanceReminderDays = reminder;
  }

  if (draft.notifyEnrollment !== settings.notifyEnrollment) {
    payload.notifyEnrollment = draft.notifyEnrollment;
  }
  if (draft.notifyBoarding !== settings.notifyBoarding) {
    payload.notifyBoarding = draft.notifyBoarding;
  }
  if (draft.notifyDropOff !== settings.notifyDropOff) {
    payload.notifyDropOff = draft.notifyDropOff;
  }
  if (draft.notifyDelay !== settings.notifyDelay) {
    payload.notifyDelay = draft.notifyDelay;
  }
  if (draft.notifyIncident !== settings.notifyIncident) {
    payload.notifyIncident = draft.notifyIncident;
  }
  if (draft.allowGuestRiders !== settings.allowGuestRiders) {
    payload.allowGuestRiders = draft.allowGuestRiders;
  }

  return payload;
}

/**
 * How transport behaves.
 *
 * <p>Two kinds of knob. The warning windows are read by the server when it
 * derives things — how close to full a route may run before the dashboard
 * complains, how far ahead a paper counts as expiring, how early a due
 * service surfaces. Changing one changes the next read, not any stored row.
 *
 * <p>The notify switches decide which guardian events are recorded at all: a
 * switch turned off is a notice that never existed, not one suppressed on the
 * way out — so turning one on does not backfill what was missed while it was
 * off.
 */
export function TransportSettingsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const manages = can(PERMISSIONS.TRANSPORT_MANAGE);

  const settings = useQuery({
    queryKey: queryKeys.transport.settings,
    queryFn: () => transportSettingsApi.get(),
  });

  const [draft, setDraft] = useState<Draft | null>(null);

  useEffect(() => {
    if (settings.data !== undefined) {
      setDraft(toDraft(settings.data));
    }
  }, [settings.data]);

  const save = useMutation({
    mutationFn: (payload: TransportSettingsInput) => transportSettingsApi.update(payload),
    onSuccess: () => {
      toast.success('Settings saved. The new thresholds apply from the next read.');
      void queryClient.invalidateQueries({ queryKey: queryKeys.transport.all });
    },
    onError: (error) => handleApiError(error),
  });

  if (settings.isError) {
    return <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />;
  }
  if (settings.isPending || draft === null) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const payload = changedFields(settings.data, draft);
  const dirty = Object.keys(payload).length > 0;

  const set = (patch: Partial<Draft>) =>
    setDraft((current) => (current === null ? current : { ...current, ...patch }));

  return (
    <div>
      <PageHeader
        title="Transport settings"
        description="Warning windows and notification switches — the knobs the server reads when it decides something."
        icon={<Settings2 className="h-5 w-5" aria-hidden="true" />}
        action={
          manages ? (
            <Button
              onClick={() => save.mutate(payload)}
              disabled={!dirty || save.isPending}
              loading={save.isPending}
            >
              Save changes
            </Button>
          ) : undefined
        }
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
            Warning windows
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            Read at derivation time. Changing one changes the next dashboard read, not any
            stored row.
          </p>

          <div className="mt-3 space-y-3">
            <TextField
              label="Capacity warning at (%)"
              type="number"
              min={50}
              max={100}
              value={draft.capacityWarningPercent}
              onChange={(event) => set({ capacityWarningPercent: event.target.value })}
              disabled={!manages}
              hint="A route at or past this share of its effective capacity raises a dashboard warning. 50 to 100."
            />
            <TextField
              label="Document expiry warning (days)"
              type="number"
              min={1}
              max={365}
              value={draft.documentExpiryWarningDays}
              onChange={(event) => set({ documentExpiryWarningDays: event.target.value })}
              disabled={!manages}
              hint="How far ahead a vehicle or driver paper counts as expiring rather than valid."
            />
            <TextField
              label="Maintenance reminder (days)"
              type="number"
              min={1}
              max={365}
              value={draft.maintenanceReminderDays}
              onChange={(event) => set({ maintenanceReminderDays: event.target.value })}
              disabled={!manages}
              hint="How early a scheduled service appears on the due list before its date."
            />
          </div>
        </Card>

        <div className="space-y-4">
          <Card className="p-4">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
              Guardian notices
            </h2>
            <p className="mt-0.5 text-xs text-muted">
              A switch turned off is a notice that never existed — turning it on later does not
              backfill what was missed.
            </p>

            <div className="mt-3 space-y-3">
              <Toggle
                label="Enrollment"
                description="When a child's service is confirmed, or their route or stop changes."
                checked={draft.notifyEnrollment}
                onChange={(checked) => set({ notifyEnrollment: checked })}
                disabled={!manages}
              />
              <Toggle
                label="Boarding"
                description="When a child is marked aboard the morning bus."
                checked={draft.notifyBoarding}
                onChange={(checked) => set({ notifyBoarding: checked })}
                disabled={!manages}
              />
              <Toggle
                label="Drop-off"
                description="When a child is marked off the afternoon bus."
                checked={draft.notifyDropOff}
                onChange={(checked) => set({ notifyDropOff: checked })}
                disabled={!manages}
              />
              <Toggle
                label="Delay"
                description="When a trip is delayed — one notice per rider on it."
                checked={draft.notifyDelay}
                onChange={(checked) => set({ notifyDelay: checked })}
                disabled={!manages}
              />
              <Toggle
                label="Incident"
                description="When an incident naming the child is escalated. Kind and severity only — never the narrative."
                checked={draft.notifyIncident}
                onChange={(checked) => set({ notifyIncident: checked })}
                disabled={!manages}
              />
            </div>
          </Card>

          <Card className="p-4">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
              Guest riders
            </h2>
            <p className="mt-0.5 text-xs text-muted">
              The school-authorized exception, and whether it exists here at all.
            </p>

            <div className="mt-3">
              <Toggle
                label="Allow guest riders"
                description="Whether attendance may be recorded for a student enrolled on a different route — a one-off ride home with a sibling. Guest rows stay flagged either way."
                checked={draft.allowGuestRiders}
                onChange={(checked) => set({ allowGuestRiders: checked })}
                disabled={!manages}
              />
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

/** A labelled switch with the sentence that explains what it actually does. */
function Toggle({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="flex items-start gap-2 text-sm text-ink">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        disabled={disabled}
        className="mt-0.5 h-4 w-4 border-line"
      />
      <span>
        {label}
        <span className="block text-[12px] text-muted">{description}</span>
      </span>
    </label>
  );
}
